import { FC, useState } from 'react';
import { useAssignSitesToOrg } from '../../graphql/useAssignSitesToOrg';
import { Options } from '../SelectElementWrapper';
import AssignSitesToOrgModal from './AssignSitesToOrgModal';
import { toast } from '@gmg/gmg-react-components';
import { useTranslation } from 'react-i18next';

export interface SiteAssignmentCandidate {
    id: string;
    name: string;
    parentOrgId: string | undefined;
    isHeadOrganization: boolean;
}

export interface AssignSitesToOrgModalContainerProps {
    // Id of the organization the "Assign sites" action was initiated from (the future parent / head organization).
    currentOrgId: string;
    // Name of the organization the "Assign sites" action was initiated from, shown in the success toast.
    currentOrgName: string;
    // All organizations, provided by the list view so we don't re-run the listOrganizations query.
    organizations: Array<SiteAssignmentCandidate>;
    // Injected by the modal framework when rendered via onShowModal.
    onClose?: () => void;
}

const mapToSelectOption = (org: SiteAssignmentCandidate): Options => ({ value: org.id, label: org.name });

export const getSelectableSiteAssignmentCandidates = (
    organizations: Array<SiteAssignmentCandidate>,
    currentOrgId: string,
) => organizations.filter(org =>
    org.id !== currentOrgId
    && !org.isHeadOrganization
    && (!org.parentOrgId || org.parentOrgId === currentOrgId));

const AssignSitesToOrgModalContainer: FC<AssignSitesToOrgModalContainerProps> = (props) => {
    const { t } = useTranslation();
    // Orgs already assigned as sites of the current org -> shown as prefilled selection.
    const assignedSites = props.organizations.filter(org => org.parentOrgId === props.currentOrgId);

    const selectableOrgs = getSelectableSiteAssignmentCandidates(props.organizations, props.currentOrgId);

    const preselectedSites = assignedSites.map(mapToSelectOption);

    const [selectedSites, setSelectedSites] = useState<Array<Options>>(preselectedSites);
    const assignSitesToOrg = useAssignSitesToOrg(props.currentOrgId);

    const initialSiteIds = new Set(preselectedSites.map(site => site.value));
    const selectedSiteIds = new Set(selectedSites.map(site => site.value));
    const isSelectionUnchanged = initialSiteIds.size === selectedSiteIds.size
        && [...initialSiteIds].every(id => selectedSiteIds.has(id));

    const handleOnSubmit = () => {
        void toast.promise(assignSitesToOrg.mutateAsync(selectedSites.map(site => String(site.value))), {
            loading: t('Common.Toast.Loading', 'Loading ...'),
            success: t('AssignSitesToOrgModal.toast_success_desc', {
                defaultValue: 'Site assignments updated for the organization {{organizationName}}.',
                organizationName: props.currentOrgName,
            }),
            error: (response) => response.errors[0].message,
        });
        props.onClose?.();
    };

    return (
        <AssignSitesToOrgModal
            siteOptions={selectableOrgs.map(mapToSelectOption)}
            selectedSites={selectedSites}
            onSelectionChange={setSelectedSites}
            onSave={handleOnSubmit}
            onClose={props.onClose}
            isSaveDisabled={isSelectionUnchanged || assignSitesToOrg.isPending}
        />
    );
};

export default AssignSitesToOrgModalContainer;
